// @ts-nocheck
// ============================================================
// TV HEALTH STORE — Zustand (Fase 4)
// Estado de saúde, device identity, métricas e reconexão
// ============================================================
import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import type { TVHealthState, PairingState, TVRenderError, ReconnectState } from '@/types/tvDevice'

const INITIAL_RECONNECT: ReconnectState = {
  attempts: 0,
  delayMs: 1_000,
  isReconnecting: false,
  lastErrorAt: null,
  mode: 'realtime',
}

const MAX_BACKOFF_MS   = 30_000
const BACKOFF_MULT     = 2
const JITTER_PCT       = 0.2
const MAX_RENDER_ERRORS = 20

function withJitter(ms: number): number {
  const jitter = ms * JITTER_PCT * (Math.random() * 2 - 1)
  return Math.min(Math.round(ms + jitter), MAX_BACKOFF_MS)
}

export const useTVHealthStore = create<TVHealthState>()(
  subscribeWithSelector((set, get) => ({
    // ─── Identity ────────────────────────────────────────────
    deviceId:          null,
    deviceToken:       null,
    deviceName:        'TV',
    isPaired:          false,
    pairingState:      'checking',
    pairingPin:        null,
    pairingExpiresAt:  null,

    // ─── Metrics ─────────────────────────────────────────────
    fps:               0,
    memoryMb:          0,
    connectionType:    'unknown',
    uptimeSec:         0,
    startedAt:         Date.now(),
    errorCount:        0,
    renderErrors:      [],

    // ─── Heartbeat ───────────────────────────────────────────
    lastHeartbeatAt:   null,
    heartbeatStatus:   'ok',

    // ─── Reconnect ───────────────────────────────────────────
    reconnect:         INITIAL_RECONNECT,

    // ─── UI ──────────────────────────────────────────────────
    showDiagnostics:   false,

    // ─── Actions ─────────────────────────────────────────────
    setDeviceIdentity: (id, token, name) =>
      set({ deviceId: id, deviceToken: token, deviceName: name }),

    setPaired: (v) => set({ isPaired: v }),

    setPairingState: (s: PairingState) => set({ pairingState: s }),

    setPairingPin: (pin, expiresAt = null) =>
      set({ pairingPin: pin, pairingExpiresAt: expiresAt }),

    setFps: (fps) => set({ fps: Math.round(fps) }),

    setMemoryMb: (mb) => set({ memoryMb: Math.round(mb * 10) / 10 }),

    setConnectionType: (t) => set({ connectionType: t }),

    tickUptime: () => set(s => ({ uptimeSec: s.uptimeSec + 1 })),

    incrementErrors: () => set(s => ({ errorCount: s.errorCount + 1 })),

    addRenderError: (err: TVRenderError) =>
      set(s => ({
        errorCount: s.errorCount + 1,
        renderErrors: [err, ...s.renderErrors].slice(0, MAX_RENDER_ERRORS),
      })),

    setLastHeartbeat: (ts, status) =>
      set({ lastHeartbeatAt: ts, heartbeatStatus: status }),

    setReconnecting: (v, attempts) =>
      set(s => ({
        reconnect: {
          ...s.reconnect,
          isReconnecting: v,
          attempts: attempts ?? s.reconnect.attempts,
          lastErrorAt: v ? new Date().toISOString() : s.reconnect.lastErrorAt,
        },
      })),

    setReconnectMode: (mode) =>
      set(s => ({ reconnect: { ...s.reconnect, mode } })),

    bumpReconnectDelay: () =>
      set(s => {
        const next = withJitter(s.reconnect.delayMs * BACKOFF_MULT)
        return {
          reconnect: {
            ...s.reconnect,
            attempts:  s.reconnect.attempts + 1,
            delayMs:   next,
            isReconnecting: false,
          },
        }
      }),

    resetReconnect: () =>
      set(s => ({
        reconnect: {
          ...INITIAL_RECONNECT,
          mode: s.reconnect.mode, // preserve polling mode if switched
        },
      })),

    toggleDiagnostics: () => set(s => ({ showDiagnostics: !s.showDiagnostics })),

    computeHealthStatus: () => {
      const { fps, errorCount, reconnect, heartbeatStatus } = get()
      if (
        heartbeatStatus === 'error' ||
        errorCount >= 5 ||
        reconnect.attempts >= 8
      ) return 'critical'
      if (
        fps < 20 ||
        heartbeatStatus === 'degraded' ||
        reconnect.attempts >= 3
      ) return 'degraded'
      return 'healthy'
    },
  })),
)

// ─── Selectors ───────────────────────────────────────────────
export const selectHealthStatus = (s: TVHealthState) => s.computeHealthStatus()

export const selectIsDeviceReady = (s: TVHealthState) =>
  s.pairingState === 'paired' && s.isPaired

export const selectReconnectInfo = (s: TVHealthState) => s.reconnect

export const selectDiagnosticsSummary = (s: TVHealthState) => ({
  deviceId:        s.deviceId,
  deviceName:      s.deviceName,
  fps:             s.fps,
  memoryMb:        s.memoryMb,
  connectionType:  s.connectionType,
  uptimeSec:       s.uptimeSec,
  errorCount:      s.errorCount,
  lastHeartbeat:   s.lastHeartbeatAt,
  heartbeatStatus: s.heartbeatStatus,
  reconnect:       s.reconnect,
  health:          s.computeHealthStatus(),
})
